import { useCallback, useEffect, useMemo, useState } from 'react';
import { App as AntdApp, Badge, Button, Popover, Space, Tag, Typography } from 'antd';
import {
  BellOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  CheckOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMockStore } from '@/mocks/store';
import { selectNotificationsForRole, type NotificationItem } from '@/mocks/selectors';
import { useRole } from '@/auth/roles';
import { semantic } from '@/theme/tokens';

const { Text } = Typography;

/**
 * Role-scoped notifications bell — Sprint 4 rewrite (S4-T02).
 *
 * Two state changes from the S1 version:
 *   1. **Read vs unread, not dismissed.** Notifications now persist on
 *      screen after being read; they fade and lose the unread dot.
 *      "Dismiss" is no longer a concept — read state is what changes.
 *   2. **localStorage persistence** of read IDs (`ga-erp-notif-read`)
 *      so reloading or switching roles doesn't reset progress.
 *
 * Plus visual polish: brand-coloured unread dot, branded severity
 * pills, hover-revealed per-row "mark read" affordance, "Mark all as
 * read" footer action, and a brand-aligned badge on the bell.
 */

const READ_KEY = 'ga-erp-notif-read';

function readReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function writeReadIds(ids: Set<string>): void {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    /* ignore quota / disabled storage */
  }
}

export default function NotificationBell() {
  const { t, i18n } = useTranslation('common');
  const { role } = useRole();
  const navigate = useNavigate();
  const state = useMockStore();
  const { message } = AntdApp.useApp();
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(() => readReadIds());
  const isRtl = i18n.language === 'ar';

  // Re-hydrate read IDs when the role changes — different roles see
  // different notification IDs and we don't want stale state colouring.
  useEffect(() => {
    setReadIds(readReadIds());
  }, [role]);

  const items = useMemo(
    () => selectNotificationsForRole(state, role),
    [state, role],
  );

  const unreadCount = items.filter((n) => !readIds.has(n.id)).length;

  const markRead = useCallback((id: string) => {
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      writeReadIds(next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    const unreadCount = items.filter((n) => !readIds.has(n.id)).length;
    setReadIds((prev) => {
      const next = new Set(prev);
      for (const n of items) next.add(n.id);
      writeReadIds(next);
      return next;
    });
    if (unreadCount > 0) {
      void message.success(t('toast.notificationsAllRead', { count: unreadCount }));
    }
  }, [items, readIds, message, t]);

  const handleClickItem = useCallback(
    (n: NotificationItem) => {
      markRead(n.id);
      if (n.to) navigate(n.to);
      setOpen(false);
    },
    [markRead, navigate],
  );

  const content = (
    <div style={{ width: 360 }}>
      {items.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('header.notificationsEmpty')}
          </Text>
        </div>
      ) : (
        <div style={{ maxHeight: 400, overflowY: 'auto', padding: '4px 0' }}>
          {items.map((n) => {
            const isUnread = !readIds.has(n.id);
            return (
              <div
                key={n.id}
                onClick={() => handleClickItem(n)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 14px',
                  borderInlineStart: `3px solid ${
                    isUnread ? severityColor(n.severity) : 'transparent'
                  }`,
                  background: isUnread ? 'rgba(31, 77, 58, 0.04)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'background 80ms ease',
                  position: 'relative',
                  opacity: isUnread ? 1 : 0.62,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(31, 77, 58, 0.07)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isUnread
                    ? 'rgba(31, 77, 58, 0.04)'
                    : 'transparent';
                }}
              >
                <span style={{ flexShrink: 0, marginTop: 1 }}>
                  {severityIcon(n.severity)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: isUnread ? 500 : 400,
                      color: isUnread ? 'rgba(26, 31, 27, 0.9)' : 'rgba(26, 31, 27, 0.65)',
                      display: 'block',
                      lineHeight: 1.45,
                    }}
                  >
                    {t(n.messageKey, n.params ?? {})}
                  </Text>
                  {n.to && (
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        marginTop: 3,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        color: isUnread ? semantic.brand.primary : 'rgba(26, 31, 27, 0.45)',
                      }}
                    >
                      {labelForRoute(n.to)}
                      <ArrowRightOutlined
                        style={{
                          fontSize: 10,
                          transform: isRtl ? 'rotate(180deg)' : 'none',
                        }}
                      />
                    </Text>
                  )}
                </div>
                {isUnread && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckOutlined style={{ fontSize: 11 }} />}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      markRead(n.id);
                    }}
                    style={{
                      flexShrink: 0,
                      color: semantic.brand.primary,
                      opacity: 0.7,
                    }}
                    aria-label={t('buttons.markAllRead')}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      <div
        style={{
          borderTop: '1px solid #efe9dd',
          padding: '8px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>
          {t('header.notificationsFooter')}
        </Text>
        <Button
          size="small"
          type="link"
          onClick={markAllRead}
          disabled={unreadCount === 0}
          style={{ fontSize: 12 }}
        >
          {t('buttons.markAllRead')}
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBlock: 4,
          }}
        >
          <Space size={6}>
            <span>{t('header.notifications')}</span>
            {unreadCount > 0 && (
              <Tag
                style={{
                  margin: 0,
                  background: 'rgba(31, 77, 58, 0.1)',
                  color: semantic.brand.primary,
                  borderColor: semantic.brand.primary,
                  fontSize: 10,
                }}
              >
                {unreadCount} {t('common:notifications.new', 'new')}
              </Tag>
            )}
          </Space>
          <Tag
            style={{
              margin: 0,
              fontSize: 10,
              borderColor: 'transparent',
              background: 'rgba(0, 0, 0, 0.04)',
              color: 'rgba(0, 0, 0, 0.55)',
            }}
          >
            {t(`roles.${role}`)}
          </Tag>
        </div>
      }
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={isRtl ? 'bottomLeft' : 'bottomRight'}
      styles={{ body: { padding: 0, minWidth: 360 } }}
    >
      <Badge
        count={unreadCount}
        size="small"
        offset={[-4, 4]}
        styles={{ root: { color: '#fff' } }}
      >
        <Button type="text" icon={<BellOutlined style={{ color: '#fff', fontSize: 16 }} />} />
      </Badge>
    </Popover>
  );
}

function severityColor(severity: NotificationItem['severity']): string {
  switch (severity) {
    case 'error':
      return semantic.status.error;
    case 'warning':
      return semantic.status.warning;
    default:
      return semantic.brand.primary;
  }
}

function severityIcon(severity: NotificationItem['severity']) {
  const color = severityColor(severity);
  const style = { fontSize: 14, color };
  switch (severity) {
    case 'error':
      return <ExclamationCircleOutlined style={style} />;
    case 'warning':
      return <WarningOutlined style={style} />;
    default:
      return <InfoCircleOutlined style={style} />;
  }
}

/**
 * Humanise a route into a "Open in Finance / AR" style chip-label.
 * Shallow mapping — falls back to the raw path's last segment.
 */
function labelForRoute(path: string): string {
  if (path.startsWith('/finance/ar')) return 'Open AR';
  if (path.startsWith('/finance/ap')) return 'Open AP';
  if (path.startsWith('/finance')) return 'Open Finance';
  if (path.startsWith('/hr/document-expiry')) return 'Open document expiry';
  if (path.startsWith('/hr/leave')) return 'Open leave';
  if (path.startsWith('/hr/attendance')) return 'Open attendance';
  if (path.startsWith('/hr/payroll')) return 'Open payroll';
  if (path.startsWith('/contracts/variation-orders')) return 'Open variation orders';
  if (path.startsWith('/contracts/claims')) return 'Open claims';
  if (path.startsWith('/contracts/po')) return 'Open POs';
  if (path.startsWith('/contracts')) return 'Open contracts';
  if (path.startsWith('/engineering/submittals')) return 'Open submittals';
  if (path.startsWith('/engineering/drawings')) return 'Open drawings';
  if (path.startsWith('/engineering/material-requests')) return 'Open MRs';
  if (path.startsWith('/marketing/leads')) return 'Open leads';
  if (path.startsWith('/marketing/tenders')) return 'Open tenders';
  if (path.startsWith('/operations/assets')) return 'Open assets';
  return `Open ${path.replace(/^\//, '').replace(/\//g, ' / ')}`;
}
